/** QuickPlan — Propriétés : instance seule (paramètres, position, rotation, étape…),
 *  sélection multiple (déplacement relatif, regroupement), ou réglages du projet. */
import { useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import NumField from './NumField.jsx';
import TextField from './TextField.jsx';
import ParamField from './ParamField.jsx';
import { instanceBBox, projectBounds, fmt, resolvedOf } from '../geom.js';
import { descendants } from '../../shared/frames.js';
import { fixingDesignation } from '../../shared/bom.js';
import { unionBBox } from '../../shared/resolve.js';
import * as ops from '../../shared/ops.js';
import { CAT_LABELS } from './LibraryPanel.jsx';
import SheetStock from './SheetStock.jsx';
import { computeCutPlan } from '../../shared/cutting.js';

const AX = ['X', 'Y', 'Z'];

function VecFields({ label, value, onCommit, step }) {
  return (
    <div className="vec">
      <span className="vec-label">{label}</span>
      {AX.map((a, k) => (
        <label key={a} className="vec-cell">
          <span>{a}</span>
          <NumField value={value[k]} step={step} onCommit={(v) => onCommit(value.map((x, j) => (j === k ? v : x)))} />
        </label>
      ))}
    </div>
  );
}

function SizeLine({ bb }) {
  if (!bb) return null;
  const s = bb.max.map((v, k) => v - bb.min[k]);
  return <div className="muted">Encombrement : {s.map((v) => fmt(v)).join(' × ')} mm</div>;
}

/** Ouverture d'une articulation (porte, tiroir) : curseur + ouvrir / fermer. */
function JointSection({ inst }) {
  const lookup = useProjectStore((s) => s.lookup)();
  const begin = useProjectStore((s) => s.beginTransient);
  const apply = useProjectStore((s) => s.applyTransient);
  const commit = useProjectStore((s) => s.commitTransient);
  const update = useProjectStore((s) => s.updateInstance);
  const res = resolvedOf(inst, lookup);
  const j = res?.joint;
  if (!j) return null;
  const unit = j.type === 'hinge' ? '°' : 'mm';
  const setOpen = (v) => apply((p) => { p.instances.find((i) => i.id === inst.id).open = Math.round(v * 10) / 10; });
  return (
    <>
      <h3>{j.type === 'hinge' ? 'Ouverture (charnière)' : 'Ouverture (glissière)'}</h3>
      <div className="row">
        <input
          type="range" className="grow" min={j.min} max={j.max} step={j.type === 'hinge' ? 1 : 5} value={inst.open}
          onPointerDown={begin} onChange={(e) => setOpen(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit}
        />
        <span className="mono">{fmt(inst.open, 0)} {unit}</span>
      </div>
      <div className="row">
        <button onClick={() => update(inst.id, { open: 0 })}>Fermer</button>
        <button onClick={() => update(inst.id, { open: j.max })}>Ouvrir</button>
        <span className="muted">double-clic dans la vue</span>
      </div>
    </>
  );
}

/** Choix d'une instance porteuse (hors `exclude` et ce qu'ils portent). */
function AttachPicker({ label, exclude, onPick }) {
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore((s) => s.lookup)();
  const banned = new Set(exclude);
  for (const id of exclude) for (const d of descendants(project, id)) banned.add(d);
  const options = project.instances.filter((i) => !banned.has(i.id) && lookup(i.componentId)?.category !== 'quincaillerie');
  return (
    <label>{label}
      <select value="" onChange={(e) => e.target.value && onPick(e.target.value)}>
        <option value="">— choisir la pièce porteuse —</option>
        {options.map((i) => <option key={i.id} value={i.id}>{i.name || lookup(i.componentId)?.name || i.componentId}</option>)}
      </select>
    </label>
  );
}

/** Attaches : porteur (détacher / changer) et pièces portées. */
function AttachSection({ inst }) {
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore((s) => s.lookup)();
  const attach = useProjectStore((s) => s.attach);
  const select = useProjectStore((s) => s.select);
  const parent = inst.parentId ? project.instances.find((i) => i.id === inst.parentId) : null;
  const children = project.instances.filter((i) => i.parentId === inst.id);
  const label = (i) => i.name || lookup(i.componentId)?.name || i.componentId;
  return (
    <>
      <h3>Attaches</h3>
      {parent ? (
        <div className="row">
          <span className="grow">Portée par <a className="link" onClick={() => select(parent.id)}>{label(parent)}</a></span>
          <button title="Détacher (la pièce reste en place)" onClick={() => attach([inst.id], null)}>Détacher</button>
        </div>
      ) : <AttachPicker label="Attacher à" exclude={[inst.id]} onPick={(pid) => attach([inst.id], pid)} />}
      {children.length > 0 && (
        <div className="muted">
          Porte {children.length} élément{children.length > 1 ? 's' : ''} :{' '}
          {children.map((c, k) => <span key={c.id}>{k > 0 && ', '}<a className="link" onClick={() => select(c.id)}>{label(c)}</a></span>)}
        </div>
      )}
    </>
  );
}

/** Emplacements de fixation prévus par le modèle (comptés en nomenclature). */
function FixingsSection({ inst }) {
  const lookup = useProjectStore((s) => s.lookup)();
  const res = resolvedOf(inst, lookup);
  if (!res?.fixings.length) return null;
  const counts = new Map();
  for (const f of res.fixings) {
    const n = fixingDesignation(f).name;
    counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  return (
    <>
      <h3>Emplacements de fixation</h3>
      <div className="muted">{[...counts.entries()].map(([n, q]) => `${q} × ${n}`).join(' · ')}</div>
    </>
  );
}

function Single({ inst }) {
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore((s) => s.lookup)();
  const update = useProjectStore((s) => s.updateInstance);
  const duplicate = useProjectStore((s) => s.duplicateSelection);
  const del = useProjectStore((s) => s.deleteSelection);
  const hide = useProjectStore((s) => s.hideSelection);
  const publish = useProjectStore((s) => s.publishComponent);
  const setStatus = useProjectStore((s) => s.setStatus);
  const comp = lookup(inst.componentId);
  const [showDesc, setShowDesc] = useState(false);
  if (!comp) return <div className="empty">Composant introuvable : {inst.componentId}</div>;
  const own = project.components.some((c) => c.id === comp.id);

  return (
    <div className="props">
      <div className="props-head">
        <span className="swatch big" style={{ background: inst.color ?? comp.color }} />
        <div className="grow">
          <div className="props-title">{comp.name}</div>
          <div className="muted">{CAT_LABELS[comp.category]}{comp.family ? ` · ${comp.family}` : ''}{comp.ref ? ` · ${comp.ref}` : ''}</div>
        </div>
      </div>
      {comp.description && (
        <div className="desc" onClick={() => setShowDesc(!showDesc)}>{showDesc ? comp.description : `${comp.description.slice(0, 90)}${comp.description.length > 90 ? '…' : ''}`}</div>
      )}
      <label>Nom
        <TextField value={inst.name} max={120} placeholder={comp.name} onCommit={(v) => update(inst.id, { name: v || undefined })} />
      </label>

      {comp.params.length > 0 && (
        <>
          <h3>Paramètres</h3>
          <div className="form-grid">
            {comp.params.map((p) => (
              <ParamField key={p.key} param={p} value={inst.params[p.key] ?? p.default} onChange={(v) => update(inst.id, { params: { [p.key]: v } })} />
            ))}
          </div>
        </>
      )}

      <h3>Placement</h3>
      <VecFields label="Position (mm)" value={inst.pos} onCommit={(pos) => update(inst.id, { pos })} />
      <VecFields label="Rotation (°)" value={inst.rot} onCommit={(rot) => update(inst.id, { rot })} />
      <div className="row wrap">
        <button title="Tourner de 90° autour de Z" onClick={() => update(inst.id, { rot: ops.composeRot([0, 0, 90], inst.rot) })}>↻ Z 90°</button>
        <button title="Tourner de 90° autour de Y" onClick={() => update(inst.id, { rot: ops.composeRot([0, 90, 0], inst.rot) })}>↻ Y 90°</button>
        <button title="Tourner de 90° autour de X" onClick={() => update(inst.id, { rot: ops.composeRot([90, 0, 0], inst.rot) })}>↻ X 90°</button>
        <button title="Remettre la rotation à zéro" onClick={() => update(inst.id, { rot: [0, 0, 0] })}>0°</button>
      </div>
      <SizeLine bb={instanceBBox(inst, lookup, project)} />

      <JointSection inst={inst} />
      <AttachSection inst={inst} />
      <FixingsSection inst={inst} />

      <h3>Organisation</h3>
      <div className="form-grid">
        <label>Sous-ensemble
          <select value={inst.groupId ?? ''} onChange={(e) => update(inst.id, { groupId: e.target.value || null })}>
            <option value="">(racine)</option>
            {project.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        <label>Étape de montage
          <NumField value={inst.step} allowNull placeholder="—" onCommit={(v) => update(inst.id, { step: v == null ? null : Math.max(1, Math.round(v)) })} />
        </label>
        <label>Couleur
          <span className="row">
            <input type="color" value={inst.color ?? comp.color} onChange={(e) => update(inst.id, { color: e.target.value })} />
            {inst.color && <button className="icon" title="Couleur du modèle" onClick={() => update(inst.id, { color: null })}>↺</button>}
          </span>
        </label>
      </div>
      <label>Note
        <TextField multiline rows={2} value={inst.note} onCommit={(v) => update(inst.id, { note: v || undefined })} />
      </label>

      <div className="row wrap actions">
        <button onClick={hide} title="Masquer (H) — ce qu'elle porte disparaît avec elle">◌ Masquer</button>
        <button onClick={duplicate} title="Dupliquer (Ctrl+D)">⧉ Dupliquer</button>
        <button className="danger" onClick={del} title="Supprimer (Suppr)">🗑 Supprimer</button>
        {own && <button title="Publier ce modèle dans la bibliothèque d'atelier" onClick={() => publish(comp).then(() => setStatus(`« ${comp.name} » publié dans l'atelier.`)).catch((e) => setStatus(`⚠ ${e.message}`))}>⇪ Publier le modèle</button>}
      </div>
    </div>
  );
}

function Multi({ ids }) {
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore((s) => s.lookup)();
  const mutate = useProjectStore((s) => s.mutate);
  const del = useProjectStore((s) => s.deleteSelection);
  const duplicate = useProjectStore((s) => s.duplicateSelection);
  const hide = useProjectStore((s) => s.hideSelection);
  const attach = useProjectStore((s) => s.attach);
  const [delta, setDelta] = useState([0, 0, 0]);
  const insts = project.instances.filter((i) => ids.includes(i.id));
  const bb = unionBBox(insts.map((i) => instanceBBox(i, lookup, project)).filter(Boolean));

  return (
    <div className="props">
      <div className="props-title">{ids.length} éléments sélectionnés</div>
      <SizeLine bb={bb} />
      <h3>Déplacer de</h3>
      <VecFields label="Δ (mm)" value={delta} onCommit={setDelta} />
      <button onClick={() => mutate((p, lk) => ops.moveInstances(p, ids, delta, lk))}>Appliquer le déplacement</button>
      <h3>Organisation</h3>
      <div className="row wrap">
        <button onClick={() => {
          const name = window.prompt('Nom du nouveau sous-ensemble :', 'Sous-ensemble');
          if (name) mutate((p) => { const g = ops.addGroup(p, name.trim()); for (const i of p.instances) if (ids.includes(i.id)) i.groupId = g.id; });
        }}>📁 Regrouper</button>
        <button onClick={() => {
          const v = window.prompt('Étape de montage pour la sélection (vide = aucune) :', '');
          if (v === null) return;
          const n = v.trim() ? Math.max(1, Math.round(Number(v))) : null;
          if (v.trim() && !Number.isFinite(n)) return;
          mutate((p) => { for (const i of p.instances) if (ids.includes(i.id)) i.step = n; });
        }}>🔢 Étape…</button>
      </div>
      <AttachPicker label="Attacher la sélection à" exclude={ids} onPick={(pid) => attach(ids.filter((x) => x !== pid), pid)} />
      <div className="row wrap actions">
        <button onClick={hide}>◌ Masquer</button>
        <button onClick={duplicate}>⧉ Dupliquer</button>
        <button className="danger" onClick={del}>🗑 Supprimer</button>
      </div>
    </div>
  );
}

function ProjectSettings() {
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore((s) => s.lookup)();
  const updateProject = useProjectStore((s) => s.updateProject);
  const s = project.settings;
  const set = (k, v) => updateProject((p) => { p.settings[k] = v; });
  const setMeta = (k, v) => updateProject((p) => { p.meta[k] = v || undefined; });
  const meta = project.meta ?? {};
  const setMode = useProjectStore((st) => st.setMode);
  const cut = computeCutPlan(project, lookup);

  return (
    <div className="props">
      <div className="props-title">Projet</div>
      <div className="muted">{project.instances.length} composant(s), {project.groups.length} sous-ensemble(s)</div>
      <SizeLine bb={projectBounds(project, lookup)} />
      <h3>Accroche</h3>
      <div className="form-grid">
        <label>Pas de grille (mm)
          <select value={s.grid} onChange={(e) => set('grid', Number(e.target.value))}>
            {[0.5, 1, 5, 10, 20, 25, 50, 100].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label>Pas angulaire (°)
          <select value={s.snapAngle} onChange={(e) => set('snapAngle', Number(e.target.value))}>
            {[1, 5, 15, 45, 90].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label className="check"><input type="checkbox" checked={s.snapPorts} onChange={(e) => set('snapPorts', e.target.checked)} /> Accrocher aux ports</label>
      </div>
      <h3>Débit des barres</h3>
      <div className="form-grid">
        <label>Barre du commerce (mm)<NumField value={s.barLength} onCommit={(v) => v > 0 && set('barLength', v)} /></label>
        <label>Trait de scie (mm)<NumField value={s.kerf} onCommit={(v) => v >= 0 && set('kerf', v)} /></label>
      </div>
      <h3>Panneaux et planches du fournisseur</h3>
      <SheetStock cut={cut} compact />
      {cut.groups.length > 0 && (
        <button onClick={() => setMode('notice')}>
          Plan de coupe : {cut.groups.reduce((a, g) => a + g.sheets.length, 0)} unité(s), {cut.groups.reduce((a, g) => a + g.count, 0)} pièce(s), {cut.cuts} trait(s) de scie →
        </button>
      )}
      <h3>Cartouche</h3>
      <div className="form-grid">
        <label>Client<TextField value={meta.client} max={120} onCommit={(v) => setMeta('client', v)} /></label>
        <label>Référence<TextField value={meta.reference} max={60} onCommit={(v) => setMeta('reference', v)} /></label>
        <label>Auteur<TextField value={meta.author} max={80} onCommit={(v) => setMeta('author', v)} /></label>
      </div>
      <label>Notes<TextField multiline rows={3} max={4000} value={meta.notes} onCommit={(v) => setMeta('notes', v)} /></label>
    </div>
  );
}

export default function PropsPanel() {
  const project = useProjectStore((s) => s.project);
  const selection = useProjectStore((s) => s.selection);
  if (selection.length === 1) {
    const inst = project.instances.find((i) => i.id === selection[0]);
    if (inst) return <Single inst={inst} />;
  }
  if (selection.length > 1) return <Multi ids={selection} />;
  return <ProjectSettings />;
}
